import React, { useEffect, useMemo, useState } from 'react';
import { IncentiveData } from 'src/reducers/leaderboardSlice';
import {
  shortNumberNotationStartingWithLakhs,
  shortNumberNotationWithoutTrailingZeroesAndRounding
} from 'src/utils/commonUtils';
import { SelectPickerOptionProps } from '../../interfaces';
import { getLeaderboardDetails } from 'src/pages/auth/AuthActions';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { addClickstreamEvent } from '../../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '../../../service/clickStream.constant';
import { Typography } from '@navi/web-ui/lib/primitives';
import RadioGroup from '@navi/web-ui/lib/components/RadioGroup';
import InfoIconOutlined from '@cp/assets/images/icons/InfoIconOutlined';
import {
  Directions,
  Tooltip as TooltipV2,
  TooltipContent,
  TooltipTrigger
} from '@cp/components/TooltipV2/TooltipV2';
import { INCENTIVE_BUCKET_GROUP } from '@cp/components/leaderboard/leaderboardConstant';
import Loader from '@cp/components/Loader/Loader';
import {
  CashEarnedChartHCProps,
  DEFAULT_OPTION,
  DEFAULT_OPTIONS,
  getMonthOptions,
  RENDERED_ON_DASHBOARD,
  tabOptions,
  VIEW_MODE
} from '../const';
import NewSlabTooltipContent from './components/NewSlabTooltipContent';
import OldSlabTooltipContent from './components/OldSlabTooltipContent';
import PieChartWithIncentive from './components/PieChartWIthIncentive';
import CashCollectionTitle from './components/CashCollectionTitle';
import Dropdown from '../../Dropdown';
import ToggleTabs from '../../toggleTabs/ToggleTabs';
import styles from '../Leaderboard.module.scss';
import { transformData } from '../utils';
import cx from 'classnames';

const CashEarnedChartHC: React.FC<CashEarnedChartHCProps> = props => {
  const {
    totalCurrentMonthCashCollected,
    totalPrevMonthCashCollected,
    currentMonthCashCollected,
    prevMonthCashCollected,
    monthlyCashCollectedBreakup,
    totalMonthlyCashCollected,
    animation = true,
    showDropdown = false,
    renderedOn,
    incentiveData,
    prevMonthIncentiveData,
    monthlyIncentiveData,
    slabBasedIncentiveData,
    prevMonthSlabBasedIncentiveData,
    monthlySlabBasedIncentiveData,
    useOldIncentiveData = true,
    useOldPrevMonthIncentiveData = true,
    useOldMonthlyIncentiveData = true
  } = props;

  const dispatch = useDispatch();
  const MONTH_OPTIONS = getMonthOptions();
  const referenceId = useSelector((state: RootState) => state.common.userData.referenceId);
  const leaderboardLoading = useSelector(
    (state: RootState) => state.leaderboard.leaderboardLoading
  );
  const [view, setView] = useState<VIEW_MODE>(VIEW_MODE.CURRENT);

  const [cashCollectedDropdownSelectedOption, setCashCollectedDropdownSelectedOption] = useState(
    MONTH_OPTIONS[MONTH_OPTIONS.length - 1]
  );
  const handleViewChange = () =>
    setView(prev => (prev == VIEW_MODE.CURRENT ? VIEW_MODE.PREVIOUS : VIEW_MODE.CURRENT));

  const [selectedRadioOption, setSelectedRadioOption] =
    useState<INCENTIVE_BUCKET_GROUP>(DEFAULT_OPTION);

  const handleRadioChange = (event: React.SyntheticEvent) => {
    const value = (event?.target as HTMLInputElement)?.value;
    setSelectedRadioOption(value as INCENTIVE_BUCKET_GROUP);

    if (referenceId) {
      dispatch(
        getLeaderboardDetails(
          referenceId,
          cashCollectedDropdownSelectedOption.value as string,
          `${value}`,
          cashCollectedDropdownSelectedOption.year as number
        )
      );
    }
  };

  const { realTimeCashCollectedForTeleLite, boostAgentFeatureFlag } = useSelector(
    (store: RootState) => store.common.featureFlags
  );

  useEffect(() => {
    if (referenceId) {
      dispatch(
        getLeaderboardDetails(
          referenceId,
          cashCollectedDropdownSelectedOption?.value as string,
          selectedRadioOption,
          cashCollectedDropdownSelectedOption.year as number
        )
      );
    }
  }, [cashCollectedDropdownSelectedOption]);

  const handleMonthChange = (selectedOption: SelectPickerOptionProps) => {
    setCashCollectedDropdownSelectedOption(selectedOption);
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Dashboard_CalenderMonthCashCollected, {
      selectedMonth: selectedOption?.value
    });
  };

  const totalCashCollected = useMemo(() => {
    return shortNumberNotationStartingWithLakhs(
      view === VIEW_MODE.CURRENT ? totalCurrentMonthCashCollected : totalPrevMonthCashCollected
    );
  }, [totalPrevMonthCashCollected, totalCurrentMonthCashCollected, view]);

  const cashCollectedInCurrOrPrevMonth = useMemo(() => {
    return view === VIEW_MODE.CURRENT
      ? currentMonthCashCollected?.map(transformData)
      : prevMonthCashCollected?.map(transformData);
  }, [currentMonthCashCollected, prevMonthCashCollected, view, selectedRadioOption]);

  const totalCashCollectedMonthly = shortNumberNotationStartingWithLakhs(totalMonthlyCashCollected);

  const cashCollectInSelectedMonth = monthlyCashCollectedBreakup?.map(transformData);

  const data = showDropdown
    ? cashCollectInSelectedMonth || []
    : cashCollectedInCurrOrPrevMonth || [];

  const options = {
    chart: {
      plotBackgroundColor: null,
      plotBorderWidth: 0,
      plotShadow: false,
      style: {
        fontFamily: 'Inter'
      },
      renderTo: 'container',
      height: 200,
      width: 300
    },
    credits: {
      enabled: false
    },
    title: {
      text: `<div class='${styles.chartTitleContainer}'>₹${
        showDropdown ? totalCashCollectedMonthly : totalCashCollected
      }</div>`,
      align: 'center',
      verticalAlign: 'middle'
    },
    tooltip: {
      pointFormat: `<b class=‘${styles.tooltip}’>{point.percentage:.1f}%</b>`
    },
    accessibility: {
      point: {
        valueSuffix: '%'
      }
    },
    plotOptions: {
      pie: {
        size: 120,
        animation,
        dataLabels: {
          enabled: true,
          allowOverlap: true,
          align: 'left',
          format: `<div class=${styles.gray}>{point.name}</div><div class=${styles.point}>₹{point.value}</div>`,
          useHTML: true,
          distance: 20,
          filter: {
            property: 'percentage',
            operator: '>',
            value: 0
          }
        }
      }
    },
    series: [
      {
        type: 'pie',
        name: 'CashEarned',
        innerSize: '70%',
        data: data || []
      }
    ]
  };

  const noDataInPieChart = useMemo(() => !data.some(value => value.y > 0), [data]);

  const [selectedIncentiveData, setSelectedIncentiveData] = useState<IncentiveData | undefined>();
  const [isShowingOldIncentiveSlabData, setIsShowingOldIncentiveSlabData] = useState(true);

  const totalCashCollectedForSelectedRadioOption = useMemo(
    () =>
      selectedRadioOption === INCENTIVE_BUCKET_GROUP.DPD_1_TO_30
        ? selectedIncentiveData?.total1To30DaysCashCollected
        : selectedRadioOption === INCENTIVE_BUCKET_GROUP.DPD_30_PLUS
        ? selectedIncentiveData?.total30PlusDaysCashCollected
        : selectedIncentiveData?.thresholdForIncentive,
    [selectedRadioOption, selectedIncentiveData]
  );

  const totalIncentiveEarnedForSelectedRadioOption = useMemo(
    () =>
      selectedRadioOption === INCENTIVE_BUCKET_GROUP.DPD_1_TO_30
        ? selectedIncentiveData?.nonBuffer1To30DaysIncentiveEarned
        : selectedRadioOption === INCENTIVE_BUCKET_GROUP.DPD_30_PLUS
        ? selectedIncentiveData?.nonBuffer30PlusIncentiveEarned
        : selectedIncentiveData?.incentiveEarned || selectedIncentiveData?.totalIncentiveEarned,
    [selectedRadioOption, selectedIncentiveData]
  );

  useEffect(() => {
    if (showDropdown) {
      if (useOldMonthlyIncentiveData) {
        setSelectedIncentiveData(monthlyIncentiveData);
        setIsShowingOldIncentiveSlabData(true);
      } else {
        setSelectedIncentiveData(monthlySlabBasedIncentiveData);
        if (selectedRadioOption === INCENTIVE_BUCKET_GROUP.TOTAL) {
          setIsShowingOldIncentiveSlabData(false);
        }
      }
    }
  }, [selectedRadioOption, monthlyIncentiveData, monthlySlabBasedIncentiveData]);

  useEffect(() => {
    const isCurrent = view === VIEW_MODE.CURRENT;
    const useOldData = isCurrent ? useOldIncentiveData : useOldPrevMonthIncentiveData;
    const newData = isCurrent ? slabBasedIncentiveData : prevMonthSlabBasedIncentiveData;
    const oldData = isCurrent ? incentiveData : prevMonthIncentiveData;

    setSelectedIncentiveData(useOldData ? oldData : newData);
    setIsShowingOldIncentiveSlabData(
      useOldData || selectedRadioOption !== INCENTIVE_BUCKET_GROUP.TOTAL
    );
  }, [view, selectedRadioOption]);

  return (
    <>
      <div className={styles.tabsContainer}>
        <>
          {showDropdown ? (
            <Dropdown
              defaultSelected={cashCollectedDropdownSelectedOption}
              options={MONTH_OPTIONS}
              onSelectionChange={handleMonthChange}
              placeholder="Select Month"
              containerClasses={styles.dropdown}
              arrowIconColor={styles.naviIconBlueColor}
              arrowIconWidth={18}
            />
          ) : (
            <ToggleTabs
              selected={view}
              onTabClick={handleViewChange}
              tabOptions={tabOptions}
              size="h6"
            />
          )}
          <CashCollectionTitle showDropdown={showDropdown} view={view} />
        </>

        <div className={styles.incentiveHeader}>
          <Typography variant="h4" className="font-bold ml-4 mr-1">
            Incentives Earned*
          </Typography>
          <TooltipV2 placement={showDropdown ? Directions.RIGHT : Directions.TOP}>
            <TooltipTrigger tooltipTriggerClassName="tooltipTriggerWrapper">
              <InfoIconOutlined fillColor="var(--blue-base)" />
            </TooltipTrigger>

            {isShowingOldIncentiveSlabData ? (
              <TooltipContent className="p-[8px] bg-[var(--grayscale-1)] z-[var(--z-index-leaderboard-tooltip)] rounded-[4px] w-[360px]">
                <OldSlabTooltipContent
                  selectedRadioOption={selectedRadioOption}
                  selectedIncentiveData={selectedIncentiveData}
                  totalCashCollectedForSelectedRadioOption={
                    totalCashCollectedForSelectedRadioOption
                  }
                  totalIncentiveEarnedForSelectedRadioOption={
                    totalIncentiveEarnedForSelectedRadioOption
                  }
                />
              </TooltipContent>
            ) : (
              <TooltipContent className="border border-[--navi-color-gray-border] ml-[5px] p-[8px] rounded-lg bg-[var(--grayscale-1)] z-[var(--z-index-leaderboard-tooltip)] w-[332px]">
                <NewSlabTooltipContent selectedIncentiveData={selectedIncentiveData} />
              </TooltipContent>
            )}
          </TooltipV2>
        </div>
      </div>

      <div
        className={cx({
          [styles.pieChart]: true,
          pieChartContainerClass: true,
          [styles.pieChartDashboardWidth]: renderedOn !== RENDERED_ON_DASHBOARD
        })}
      >
        <>
          <PieChartWithIncentive
            realTimeCashCollectedForTeleLite={realTimeCashCollectedForTeleLite}
            showDropdown={showDropdown}
            totalIncentiveEarnedForSelectedRadioOption={totalIncentiveEarnedForSelectedRadioOption}
            shortNumberNotationWithoutTrailingZeroesAndRounding={
              shortNumberNotationWithoutTrailingZeroesAndRounding
            }
            renderedOn={renderedOn}
            RENDERED_ON_DASHBOARD={RENDERED_ON_DASHBOARD}
            options={options}
            noDataInPieChart={noDataInPieChart}
          />

          {realTimeCashCollectedForTeleLite || boostAgentFeatureFlag ? null : (
            <RadioGroup
              className={styles.radioContainer}
              onChange={handleRadioChange}
              value={selectedRadioOption}
              name="cashCollected"
              options={DEFAULT_OPTIONS}
              labelClassName=""
            />
          )}

          <Typography variant="p5" className={styles.incentiveDisclaimerText}>
            *Subject to month end adjustments
          </Typography>
        </>
      </div>

      <Loader show={leaderboardLoading || false} className={styles.loadingState} animate={false} />
    </>
  );
};
export default CashEarnedChartHC;
